import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  X, 
  Send, 
  Eye, 
  Paperclip, 
  Users, 
  Filter,
  TestTube,
  Save,
  FileText,
  Bold,
  Italic,
  Underline,
  List,
  Link,
  Eraser,
  AlignLeft,
  AlignCenter,
  AlignRight,
  ListOrdered,
  Quote,
  Code,
  Undo,
  Redo
} from 'lucide-react';
import { useToastContext } from '../../context/ToastContext';
import { attendeeAPI, eventAPI } from '../../api/endpoints';
import { getApiUrl } from '../../utils/apiConfig';

const EmailComposer = ({ 
  isOpen, 
  onClose, 
  eventId = null, 
  attendees = [], 
  onSendComplete,
  initialData = null 
}) => {
  const toast = useToastContext();
  const [campaign, setCampaign] = useState({
    name: '',
    subject: '',
    content: '',
    recipient_type: 'all',
    recipient_filters: [],
    individual_recipients: [],
    from_name: '',
    from_email: '',
    reply_to: '',
    track_opens: true,
    track_clicks: true,
    ...initialData
  });

  const [attachments, setAttachments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewContent, setPreviewContent] = useState('');
  const [templateVariables, setTemplateVariables] = useState({});
  const [recipientCount, setRecipientCount] = useState(0);
  const [testEmail, setTestEmail] = useState('');
  const [selectedEvent, setSelectedEvent] = useState(eventId || '');

  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);

  // Fetch events with counts (multitenant pattern)
  const { data: eventsData } = useQuery({
    queryKey: ['events-with-counts'],
    queryFn: async () => {
      console.log('🎆 Fetching events for email composer...');
      const response = await eventAPI.getAll();
      console.log('🎆 Events response:', response);
      const events = response.data.events || [];
      
      // Fetch attendee counts for all events in parallel
      const eventCountPromises = events.map(async (event) => {
        try {
          const attendeesResponse = await attendeeAPI.getAll(event.id);
          return {
            ...event,
            realAttendeeCount: attendeesResponse.data.attendees?.length || 0
          };
        } catch (error) {
          console.warn(`Failed to fetch attendees for event ${event.id}:`, error);
          return {
            ...event,
            realAttendeeCount: event.attendeeCount || 0
          };
        }
      });
      
      const eventsWithCounts = await Promise.all(eventCountPromises);
      
      return { events: eventsWithCounts };
    },
    enabled: isOpen, // Only fetch when modal is open
  });

  // Fetch attendees for selected event (multitenant pattern)
  const { data: attendeesData } = useQuery({
    queryKey: ['attendees', selectedEvent],
    queryFn: async () => {
      if (!selectedEvent) return { attendees: [] };
      
      console.log('🔍 Fetching attendees for email composer event:', selectedEvent);
      
      try {
        const response = await attendeeAPI.getAll(selectedEvent);
        console.log('✅ Email composer attendees API response:', response);
        return response.data;
      } catch (error) {
        console.error('❌ Email composer attendees API error:', error);
        throw error;
      }
    },
    enabled: !!selectedEvent && isOpen,
  });

  // Extract data from queries with useMemo
  const events = useMemo(() => eventsData?.events || [], [eventsData?.events]);
  const eventAttendees = useMemo(() => attendeesData?.attendees || [], [attendeesData?.attendees]);

  // Define functions before using them in useEffect
  const loadTemplateVariables = useCallback(async () => {
    if (!selectedEvent) return;
    
    try {
      const response = await fetch(`/api/events/${selectedEvent}/template-variables`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setTemplateVariables(data.data || {});
      }
    } catch (error) {
      console.error('Failed to load template variables:', error);
    }
  }, [selectedEvent]);

  const calculateRecipientCount = useCallback(() => {
    switch (campaign.recipient_type) {
      case 'all':
        setRecipientCount(eventAttendees.length);
        break;
      case 'filtered':
        // Apply filters to attendees
        let filtered = eventAttendees;
        campaign.recipient_filters.forEach(filter => {
          // Apply filter logic here
          // This is a simplified version
          filtered = filtered.filter(attendee => {
            const value = attendee[filter.field];
            switch (filter.operator) {
              case 'equals':
                return value === filter.value;
              case 'contains':
                return value && value.toLowerCase().includes(filter.value.toLowerCase());
              default:
                return true;
            }
          });
        });
        setRecipientCount(filtered.length);
        break;
      case 'individual':
        setRecipientCount(campaign.individual_recipients.length);
        break;
      default:
        setRecipientCount(0);
    }
  }, [campaign.recipient_type, campaign.recipient_filters, campaign.individual_recipients, eventAttendees]);

  // Enhanced formatting functions with Gmail-style behavior
  const insertVariable = (variable) => {
    if (textareaRef.current) {
      textareaRef.current.focus();
      
      // Create a span element for the variable with Gmail-style styling
      const variableSpan = document.createElement('span');
      variableSpan.className = 'variable-tag';
      variableSpan.textContent = `{{${variable}}}`;
      variableSpan.contentEditable = 'false'; // Prevent editing of variables
      
      // Insert the variable at cursor position
      insertNodeAtCursor(variableSpan);
      updateContent();
    }
  };

  // Helper function to insert node at cursor position
  const insertNodeAtCursor = (node) => {
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      range.deleteContents();
      range.insertNode(node);
      
      // Add a space after the node for better UX
      const spaceNode = document.createTextNode(' ');
      range.setStartAfter(node);
      range.insertNode(spaceNode);
      range.setStartAfter(spaceNode);
      range.setEndAfter(spaceNode);
      
      selection.removeAllRanges();
      selection.addRange(range);
    } else {
      textareaRef.current.appendChild(node);
    }
  };

  // Update content and maintain cursor position
  const updateContent = (skipHistory = false) => {
    if (textareaRef.current) {
      const newContent = textareaRef.current.innerHTML;
      setCampaign(prev => ({ ...prev, content: newContent }));
      
      // Add to history if not an undo/redo operation
      if (!skipHistory && !isUndoRedo) {
        addToHistory(newContent);
      }
    }
  };
  
  
  // Debounced history addition to prevent too many entries during rapid typing
  const historyTimeoutRef = useRef(null);
  
  // Add content to history for undo/redo
  const addToHistory = (content) => {
    // Clear existing timeout
    if (historyTimeoutRef.current) {
      clearTimeout(historyTimeoutRef.current);
    }
    
    // Debounce history addition by 500ms
    historyTimeoutRef.current = setTimeout(() => {
      setHistory(prev => {
        // Don't add if content is the same as the last entry
        if (prev.length > 0 && prev[prev.length - 1] === content) {
          return prev;
        }
        
        const newHistory = prev.slice(0, historyIndex + 1);
        newHistory.push(content);
        
        // Limit history to 50 items to prevent memory issues
        if (newHistory.length > 50) {
          newHistory.shift();
          setHistoryIndex(prev => prev - 1);
        }
        
        return newHistory;
      });
      setHistoryIndex(prev => prev + 1);
    }, 500);
  };
  
  // Undo function
  const handleUndo = () => {
    if (historyIndex > 0) {
      setIsUndoRedo(true);
      const prevContent = history[historyIndex - 1];
      textareaRef.current.innerHTML = prevContent;
      setCampaign(prev => ({ ...prev, content: prevContent }));
      setHistoryIndex(prev => prev - 1);
      
      // Reset undo/redo flag after a short delay
      setTimeout(() => setIsUndoRedo(false), 100);
    }
  };
  
  // Redo function
  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      setIsUndoRedo(true);
      const nextContent = history[historyIndex + 1];
      textareaRef.current.innerHTML = nextContent;
      setCampaign(prev => ({ ...prev, content: nextContent }));
      setHistoryIndex(prev => prev + 1);
      
      // Reset undo/redo flag after a short delay
      setTimeout(() => setIsUndoRedo(false), 100);
    }
  };

  // Auto-detect and convert URLs to links (Gmail-style) - improved to avoid double-linking
  const autoLinkUrls = (htmlContent) => {
    // Don't process if content already has links or is empty
    if (!htmlContent || htmlContent.includes('<a ')) {
      return htmlContent;
    }
    
    const urlRegex = /(https?:\/\/[^\s<>"]+|www\.[^\s<>"]+)/gi;
    return htmlContent.replace(urlRegex, (url) => {
      const href = url.startsWith('http') ? url : `https://${url}`;
      return `<a href="${href}" target="_blank" rel="noopener noreferrer" style="color: #1a73e8; text-decoration: none;">${url}</a>`;
    });
  };

  // Handle paste events to auto-link URLs
  const handlePaste = (e) => {
    e.preventDefault();
    const paste = e.clipboardData.getData('text/plain');
    const linkedText = autoLinkUrls(paste);
    
    // Insert the linked text
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      range.deleteContents();
      
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = linkedText;
      
      while (tempDiv.firstChild) {
        range.insertNode(tempDiv.firstChild);
      }
    }
    
    updateContent();
  };

  // Gmail-style formatting with proper toggle behavior and state detection
  const formatText = (format, value = null) => {
    if (!textareaRef.current) return;
    
    textareaRef.current.focus();
    
    try {
      switch (format) {
        case 'bold':
          document.execCommand('bold', false, null);
          break;
        case 'italic':
          document.execCommand('italic', false, null);
          break;
        case 'underline':
          document.execCommand('underline', false, null);
          break;
        case 'link':
          handleLinkInsertion();
          break;
        case 'unorderedList':
          handleListToggle('unordered');
          break;
        case 'orderedList':
          handleListToggle('ordered');
          break;
        case 'alignLeft':
          document.execCommand('justifyLeft', false, null);
          break;
        case 'alignCenter':
          document.execCommand('justifyCenter', false, null);
          break;
        case 'alignRight':
          document.execCommand('justifyRight', false, null);
          break;
        case 'alignJustify':
          document.execCommand('justifyFull', false, null);
          break;
        case 'fontSize':
          if (value) document.execCommand('fontSize', false, value);
          break;
        case 'fontColor':
          if (value) document.execCommand('foreColor', false, value);
          break;
        case 'backgroundColor':
          if (value) document.execCommand('backColor', false, value);
          break;
        case 'blockquote':
          handleBlockquoteToggle();
          break;
        case 'code':
          handleCodeToggle();
          break;
        case 'removeFormat':
          document.execCommand('removeFormat', false, null);
          // Also remove custom elements
          removeCustomFormatting();
          break;
        default:
          return;
      }
      
      updateContent();
      
    } catch (error) {
      console.warn('Format command not supported:', format, error);
    }
  };

  // Handle list toggle with proper Gmail-like behavior
  const handleListToggle = (type) => {
    const selection = window.getSelection();
    if (selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    const container = range.commonAncestorContainer;
    
    // Find if we're already in a list
    let currentList = container.nodeType === Node.TEXT_NODE ? container.parentElement : container;
    while (currentList && currentList !== textareaRef.current) {
      if (currentList.tagName === 'UL' || currentList.tagName === 'OL') {
        break;
      }
      currentList = currentList.parentElement;
    }

    if (currentList && (currentList.tagName === 'UL' || currentList.tagName === 'OL')) {
      // We're in a list - check if it's the same type
      const isUnordered = currentList.tagName === 'UL';
      const isSameType = (type === 'unordered' && isUnordered) || (type === 'ordered' && !isUnordered);
      
      if (isSameType) {
        // Same type - remove list formatting
        document.execCommand(type === 'unordered' ? 'insertUnorderedList' : 'insertOrderedList', false, null);
      } else {
        // Different type - convert list
        document.execCommand(type === 'unordered' ? 'insertUnorderedList' : 'insertOrderedList', false, null);
      }
    } else {
      // Not in a list - create new list
      document.execCommand(type === 'unordered' ? 'insertUnorderedList' : 'insertOrderedList', false, null);
    }
  };

  // Handle blockquote toggle with proper Gmail-like behavior
  const handleBlockquoteToggle = () => {
    const selection = window.getSelection();
    if (selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    let container = range.commonAncestorContainer;
    
    // Find if we're already in a blockquote
    if (container.nodeType === Node.TEXT_NODE) {
      container = container.parentElement;
    }
    
    let blockquote = container;
    while (blockquote && blockquote !== textareaRef.current) {
      if (blockquote.tagName === 'BLOCKQUOTE') {
        break;
      }
      blockquote = blockquote.parentElement;
    }

    if (blockquote && blockquote.tagName === 'BLOCKQUOTE') {
      // We're in a blockquote - remove it
      const content = blockquote.innerHTML;
      const newDiv = document.createElement('div');
      newDiv.innerHTML = content;
      blockquote.parentNode.replaceChild(newDiv, blockquote);
      
      // Unwrap the div content
      while (newDiv.firstChild) {
        newDiv.parentNode.insertBefore(newDiv.firstChild, newDiv);
      }
      newDiv.parentNode.removeChild(newDiv);
    } else {
      // Not in blockquote - create one
      try {
        document.execCommand('formatBlock', false, 'blockquote');
      } catch (e) {
        // Fallback method
        if (selection.toString()) {
          const selectedContent = range.extractContents();
          const blockquoteEl = document.createElement('blockquote');
          blockquoteEl.style.cssText = 'margin: 1em 0; padding: 0.5em 1em; border-left: 4px solid #e8eaed; background: #f8f9fa; font-style: italic;';
          blockquoteEl.appendChild(selectedContent);
          range.insertNode(blockquoteEl);
        }
      }
    }
  };

  // Handle code toggle with proper Gmail-like behavior
  const handleCodeToggle = () => {
    const selection = window.getSelection();
    if (selection.rangeCount === 0 || !selection.toString()) return;

    const range = selection.getRangeAt(0);
    const container = range.commonAncestorContainer;
    
    // Check if selection is already in a code element
    let codeElement = container.nodeType === Node.TEXT_NODE ? container.parentElement : container;
    while (codeElement && codeElement !== textareaRef.current) {
      if (codeElement.tagName === 'CODE') {
        break;
      }
      codeElement = codeElement.parentElement;
    }

    if (codeElement && codeElement.tagName === 'CODE') {
      // Remove code formatting
      const textContent = codeElement.textContent;
      const textNode = document.createTextNode(textContent);
      codeElement.parentNode.replaceChild(textNode, codeElement);
    } else {
      // Add code formatting
      const selectedText = selection.toString();
      const codeEl = document.createElement('code');
      codeEl.style.cssText = 'background-color: #f1f3f4; padding: 2px 4px; border-radius: 3px; font-family: Monaco, Consolas, monospace; font-size: 13px;';
      codeEl.textContent = selectedText;
      
      range.deleteContents();
      range.insertNode(codeEl);
      
      // Move cursor after the code element
      range.setStartAfter(codeEl);
      range.setEndAfter(codeEl);
      selection.removeAllRanges();
      selection.addRange(range);
    }
  };

  // Remove custom formatting elements
  const removeCustomFormatting = () => {
    const codeElements = textareaRef.current.querySelectorAll('code');
    codeElements.forEach(code => {
      const textNode = document.createTextNode(code.textContent);
      code.parentNode.replaceChild(textNode, code);
    });
  };

  // Get current formatting state for button highlighting
  const getFormattingState = () => {
    if (!textareaRef.current) return {};
    
    try {
      const selection = window.getSelection();
      if (selection.rangeCount === 0) return {};
      
      const range = selection.getRangeAt(0);
      let container = range.commonAncestorContainer;
      if (container.nodeType === Node.TEXT_NODE) {
        container = container.parentElement;
      }
      
      // Check for various formatting states
      const state = {
        bold: document.queryCommandState('bold'),
        italic: document.queryCommandState('italic'),
        underline: document.queryCommandState('underline'),
        unorderedList: document.queryCommandState('insertUnorderedList'),
        orderedList: document.queryCommandState('insertOrderedList'),
      };
      
      // Check for blockquote
      let element = container;
      while (element && element !== textareaRef.current) {
        if (element.tagName === 'BLOCKQUOTE') {
          state.blockquote = true;
          break;
        }
        element = element.parentElement;
      }
      
      // Check for code
      element = container;
      while (element && element !== textareaRef.current) {
        if (element.tagName === 'CODE') {
          state.code = true;
          break;
        }
        element = element.parentElement;
      }
      
      return state;
    } catch (error) {
      return {};
    }
  };

  // Track formatting state
  const [formattingState, setFormattingState] = useState({});
  
  // Undo/Redo history management
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [isUndoRedo, setIsUndoRedo] = useState(false);
  
  // Update formatting state on selection change
  const handleSelectionChange = useCallback(() => {
    const state = getFormattingState();
    setFormattingState(state);
  }, []);
  
  // Add selection change listener
  useEffect(() => {
    document.addEventListener('selectionchange', handleSelectionChange);
    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
    };
  }, [handleSelectionChange]);

  // State for Gmail-style link dialog
  const [linkDialog, setLinkDialog] = useState({
    isOpen: false,
    selectedText: '',
    url: '',
    range: null
  });
  
  // Track when clicking on links to prevent auto-linking interference
  const [isClickingLink, setIsClickingLink] = useState(false);

  // Gmail-style link insertion with inline dialog
  const handleLinkInsertion = () => {
    const selection = window.getSelection();
    const selectedText = selection.toString();
    
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0).cloneRange();
      setLinkDialog({
        isOpen: true,
        selectedText,
        url: '',
        range
      });
    }
  };

  // Handle link dialog submission
  const handleLinkSubmit = () => {
    const { url, selectedText, range } = linkDialog;
    
    if (!url.trim()) {
      setLinkDialog(prev => ({ ...prev, isOpen: false }));
      return;
    }
    
    const href = url.startsWith('http') ? url : `https://${url}`;
    
    // Restore selection
    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);
    
    if (selectedText) {
      // Wrap selected text with link
      document.execCommand('createLink', false, href);
      // Style the link
      const links = textareaRef.current.querySelectorAll('a[href="' + href + '"]');
      links.forEach(link => {
        link.style.color = '#1a73e8';
        link.style.textDecoration = 'none';
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
      });
    } else {
      // Insert new link
      const linkElement = document.createElement('a');
      linkElement.href = href;
      linkElement.target = '_blank';
      linkElement.rel = 'noopener noreferrer';
      linkElement.style.color = '#1a73e8';
      linkElement.style.textDecoration = 'none';
      linkElement.textContent = url;
      
      range.deleteContents();
      range.insertNode(linkElement);
      
      // Move cursor after the link
      range.setStartAfter(linkElement);
      range.setEndAfter(linkElement);
      selection.removeAllRanges();
      selection.addRange(range);
    }
    
    updateContent();
    setLinkDialog({ isOpen: false, selectedText: '', url: '', range: null });
    textareaRef.current.focus();
  };

  // Handle link dialog cancel
  const handleLinkCancel = () => {
    setLinkDialog({ isOpen: false, selectedText: '', url: '', range: null });
    textareaRef.current.focus();
  };

  // Handle Enter key in link dialog
  const handleLinkKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleLinkSubmit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleLinkCancel();
    }
  };


  // Initialize history with initial content
  useEffect(() => {
    if (campaign.content && history.length === 0) {
      setHistory([campaign.content]);
      setHistoryIndex(0);
    }
  }, [campaign.content, history.length]);
  
  // Auto-select first event when events are loaded (multitenant pattern)
  useEffect(() => {
    if (!selectedEvent && events.length > 0) {
      setSelectedEvent(events[0].id);
    }
  }, [events, selectedEvent]);

  // Load template variables when event changes
  useEffect(() => {
    if (selectedEvent) {
      loadTemplateVariables();
    }
  }, [selectedEvent, loadTemplateVariables]);

  // Calculate recipient count when filters change
  useEffect(() => {
    calculateRecipientCount();
  }, [calculateRecipientCount]);

  // Sync editor content when campaign content changes from outside
  useEffect(() => {
    if (textareaRef.current && textareaRef.current.innerHTML !== campaign.content) {
      const wasEmpty = textareaRef.current.innerHTML === '';
      textareaRef.current.innerHTML = campaign.content;
      
      // Only focus if the editor was empty (initial load)
      if (wasEmpty && campaign.content === '') {
        textareaRef.current.focus();
      }
    }
  }, [campaign.content]);

  const handleFileAttachment = (event) => {
    const files = Array.from(event.target.files);
    const newAttachments = files.map(file => ({
      id: Date.now() + Math.random(),
      file,
      name: file.name,
      size: file.size,
      type: file.type
    }));
    
    setAttachments(prev => [...prev, ...newAttachments]);
  };

  const removeAttachment = (id) => {
    setAttachments(prev => prev.filter(att => att.id !== id));
  };

  const handlePreview = async () => {
    if (!selectedEvent) {
      toast.error('Please select an event first');
      return;
    }

    if (!campaign.content.trim()) {
      toast.error('Please add some content to preview');
      return;
    }

    setIsLoading(true);
    try {
      const previewUrl = getApiUrl(`events/${selectedEvent}/email-preview`);
      console.log('📧 Generating preview at:', previewUrl);
      
      const response = await fetch(previewUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          content: campaign.content
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setPreviewContent(data.data.content);
        setShowPreview(true);
      } else {
        toast.error('Failed to generate preview');
      }
    } catch (error) {
      console.error('Preview error:', error);
      toast.error('Failed to generate preview');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendTest = async () => {
    if (!selectedEvent) {
      toast.error('Please select an event first');
      return;
    }

    if (!testEmail) {
      toast.error('Please enter a test email address');
      return;
    }

    if (!campaign.subject || !campaign.content) {
      toast.error('Please add subject and content');
      return;
    }

    setIsLoading(true);
    try {
      // First create/update the campaign
      const campaignData = new FormData();
      Object.keys(campaign).forEach(key => {
        if (campaign[key] !== null && campaign[key] !== undefined) {
          campaignData.append(key, typeof campaign[key] === 'object' ? JSON.stringify(campaign[key]) : campaign[key]);
        }
      });

      // Add attachments
      attachments.forEach(attachment => {
        campaignData.append('attachments[]', attachment.file);
      });

      const campaignUrl = getApiUrl(`events/${selectedEvent}/email-campaigns`);
      console.log('📧 Creating test campaign at:', campaignUrl);
      
      const campaignResponse = await fetch(campaignUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: campaignData,
      });

      if (campaignResponse.ok) {
        const campaignResult = await campaignResponse.json();
        
        // Send test email
        const testUrl = getApiUrl(`events/${selectedEvent}/email-campaigns/${campaignResult.data.id}/test`);
        console.log('📧 Sending test email at:', testUrl);
        
        const testResponse = await fetch(testUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
          body: JSON.stringify({
            test_email: testEmail
          }),
        });

        if (testResponse.ok) {
          toast.success(`Test email sent to ${testEmail}`);
        } else {
          toast.error('Failed to send test email');
        }
      } else {
        toast.error('Failed to create campaign for testing');
      }
    } catch (error) {
      console.error('Test email error:', error);
      toast.error('Failed to send test email');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSend = async () => {
    if (!selectedEvent) {
      toast.error('Please select an event first');
      return;
    }

    if (!campaign.name || !campaign.subject || !campaign.content) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (recipientCount === 0) {
      toast.error('No recipients selected');
      return;
    }

    const confirmed = window.confirm(`Send email to ${recipientCount} recipients?`);
    if (!confirmed) return;

    setIsLoading(true);
    try {
      const campaignData = new FormData();
      Object.keys(campaign).forEach(key => {
        if (campaign[key] !== null && campaign[key] !== undefined) {
          campaignData.append(key, typeof campaign[key] === 'object' ? JSON.stringify(campaign[key]) : campaign[key]);
        }
      });

      // Add attachments
      attachments.forEach(attachment => {
        campaignData.append('attachments[]', attachment.file);
      });

      const campaignUrl = getApiUrl(`events/${selectedEvent}/email-campaigns`);
      console.log('📧 Creating campaign at:', campaignUrl);
      
      const campaignResponse = await fetch(campaignUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: campaignData,
      });

      if (campaignResponse.ok) {
        const campaignResult = await campaignResponse.json();
        
        // Send the campaign
        const sendUrl = getApiUrl(`events/${selectedEvent}/email-campaigns/${campaignResult.data.id}/send`);
        console.log('📧 Sending campaign at:', sendUrl);
        
        const sendResponse = await fetch(sendUrl, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
        });

        if (sendResponse.ok) {
          toast.success(`Email campaign sent to ${recipientCount} recipients`);
          onSendComplete && onSendComplete();
          onClose();
        } else {
          toast.error('Failed to send email campaign');
        }
      } else {
        toast.error('Failed to create email campaign');
      }
    } catch (error) {
      console.error('Send email error:', error);
      toast.error('Failed to send email campaign');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!selectedEvent) {
      toast.error('Please select an event first');
      return;
    }

    if (!campaign.name) {
      toast.error('Please enter a campaign name');
      return;
    }

    setIsLoading(true);
    try {
      const campaignData = new FormData();
      Object.keys(campaign).forEach(key => {
        if (campaign[key] !== null && campaign[key] !== undefined) {
          campaignData.append(key, typeof campaign[key] === 'object' ? JSON.stringify(campaign[key]) : campaign[key]);
        }
      });

      // Add attachments
      attachments.forEach(attachment => {
        campaignData.append('attachments[]', attachment.file);
      });

      const url = getApiUrl(`events/${selectedEvent}/email-campaigns`);
      console.log('📧 Saving draft to:', url);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: campaignData,
      });

      if (response.ok) {
        toast.success('Draft saved successfully');
      } else {
        toast.error('Failed to save draft');
      }
    } catch (error) {
      console.error('Save draft error:', error);
      toast.error('Failed to save draft');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <style>{`
        .gmail-composer {
          font-family: 'Google Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }
        .gmail-composer [contenteditable]:focus {
          outline: none;
          border: none;
        }
        .gmail-composer .toolbar-btn {
          padding: 6px 8px;
          border: 1px solid transparent;
          border-radius: 4px;
          background: transparent;
          color: #5f6368;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
        }
        .gmail-composer .toolbar-btn:hover {
          background: #f1f3f4;
          color: #202124;
        }
        .gmail-composer .toolbar-btn.active {
          background: #e8f0fe;
          color: #1a73e8;
          border-color: #dadce0;
        }
        .gmail-composer .toolbar-btn.active:hover {
          background: #d2e3fc;
        }
        .gmail-composer .toolbar-btn:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }
        .gmail-composer .toolbar-btn:disabled:hover {
          background: transparent;
          color: #5f6368;
        }
        .gmail-composer .link-dialog {
          background: white;
          border: 1px solid #dadce0;
          border-radius: 8px;
          box-shadow: 0 4px 16px rgba(0,0,0,0.2);
          z-index: 1000;
        }
        .gmail-composer [contenteditable]:empty:before {
          content: attr(data-placeholder);
          color: #5f6368;
          font-style: italic;
        }
        .gmail-composer [contenteditable] {
          border: none;
          background: white;
          font-size: 14px;
          line-height: 1.6;
          color: #202124;
          font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        }
        .gmail-composer [contenteditable] p {
          margin: 0 0 1em 0;
        }
        .gmail-composer [contenteditable] ul, 
        .gmail-composer [contenteditable] ol {
          margin: 1em 0;
          padding-left: 2em;
        }
        .gmail-composer [contenteditable] li {
          margin: 0.25em 0;
        }
        .gmail-composer [contenteditable] a {
          color: #1a73e8;
          text-decoration: none;
          border-bottom: 1px solid transparent;
          transition: all 0.2s;
          cursor: pointer;
        }
        .gmail-composer [contenteditable] a:hover {
          border-bottom-color: #1a73e8;
          background-color: rgba(26, 115, 232, 0.1);
          border-radius: 2px;
        }
        .gmail-composer [contenteditable] blockquote {
          margin: 1em 0;
          padding: 0.5em 1em;
          border-left: 4px solid #e8eaed;
          background: #f8f9fa;
          font-style: italic;
        }
        .gmail-composer [contenteditable] code {
          background-color: #f1f3f4;
          padding: 2px 4px;
          border-radius: 3px;
          font-family: 'Monaco', 'Consolas', monospace;
          font-size: 13px;
        }
        .gmail-composer [contenteditable] h1,
        .gmail-composer [contenteditable] h2,
        .gmail-composer [contenteditable] h3 {
          margin: 1em 0 0.5em 0;
          font-weight: 600;
        }
        .gmail-composer [contenteditable] h1 { font-size: 1.5em; }
        .gmail-composer [contenteditable] h2 { font-size: 1.3em; }
        .gmail-composer [contenteditable] h3 { font-size: 1.1em; }
        .gmail-composer .variable-tag {
          background: #e8f0fe;
          color: #1a73e8;
          padding: 2px 6px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 500;
          display: inline-block;
          margin: 0 2px;
        }
        .gmail-composer .compose-header {
          background: #f8f9fa;
          border-bottom: 1px solid #e8eaed;
        }
        .gmail-composer .compose-footer {
          background: #f8f9fa;
          border-top: 1px solid #e8eaed;
        }
      `}</style>
      <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50 p-2 sm:p-4 overflow-y-auto">
        <div className="bg-white rounded-lg shadow-2xl w-full max-w-5xl my-4 flex flex-col gmail-composer" style={{ maxHeight: 'calc(100vh - 2rem)' }}>
        {/* Header - Gmail Style */}
        <div className="compose-header flex items-center justify-between px-6 py-4">
          <h2 className="text-lg font-medium text-gray-800">
            New Campaign
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-200 rounded-full transition-colors"
            title="Close"
          >
            <X className="w-4 h-4 text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto flex flex-col lg:flex-row min-h-0">
          {/* Main Composer */}
          <div className="flex-1 flex flex-col overflow-y-auto lg:overflow-y-visible">
            {/* Event Selection */}
            <div className="p-3 sm:p-4 border-b border-gray-200 flex-shrink-0">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Select Event *
                  </label>
                  <select
                    value={selectedEvent}
                    onChange={(e) => setSelectedEvent(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  >
                    <option value="">Choose an event...</option>
                    {events.map(event => (
                      <option key={event.id} value={event.id}>
                        {event.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex items-end">
                  <div className="bg-teal-50 border border-teal-200 rounded-md px-3 py-2 w-full">
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-teal-600" />
                      <span className="text-sm text-teal-800">
                        <strong>{eventAttendees.length}</strong> attendees available
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Campaign Details */}
            <div className="p-3 sm:p-4 border-b border-gray-200 space-y-3 flex-shrink-0">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Campaign Name *
                  </label>
                  <input
                    type="text"
                    value={campaign.name}
                    onChange={(e) => setCampaign(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                    placeholder="Enter campaign name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Subject Line *
                  </label>
                  <input
                    type="text"
                    value={campaign.subject}
                    onChange={(e) => setCampaign(prev => ({ ...prev, subject: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                    placeholder="Enter email subject"
                  />
                </div>
              </div>

              {/* Recipients */}
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-md">
                  <Users className="w-4 h-4 text-gray-500" />
                  <span className="text-sm text-gray-700">
                    Recipients: <strong className="text-teal-600">{recipientCount}</strong>
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <select
                    value={campaign.recipient_type}
                    onChange={(e) => setCampaign(prev => ({ ...prev, recipient_type: e.target.value }))}
                    className="flex-1 sm:flex-none px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white"
                  >
                    <option value="all">All Attendees</option>
                    <option value="filtered">Filtered</option>
                    <option value="individual">Individual</option>
                  </select>
                  {campaign.recipient_type !== 'all' && (
                    <button
                      onClick={() => toast.info('Recipient filtering coming soon!')}
                      className="px-3 py-2 bg-teal-600 text-white rounded-md text-sm hover:bg-teal-700 transition-colors flex items-center gap-1"
                    >
                      <Filter className="w-4 h-4" />
                      <span className="hidden sm:inline">Configure</span>
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Editor */}
            <div className="flex-shrink-0 p-3 sm:p-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Content *
              </label>
              
              {/* Enhanced Gmail-Style Formatting Toolbar */}
              <div className="formatting-toolbar px-4 py-2 flex items-center gap-1 flex-wrap">
                {/* Text Formatting */}
                {/* Undo/Redo */}
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={handleUndo}
                    disabled={historyIndex <= 0}
                    className="toolbar-btn"
                    title="Undo (Ctrl+Z)"
                  >
                    <Undo className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={handleRedo}
                    disabled={historyIndex >= history.length - 1}
                    className="toolbar-btn"
                    title="Redo (Ctrl+Y)"
                  >
                    <Redo className="w-4 h-4" />
                  </button>
                </div>
                
                <div className="w-px h-4 bg-gray-300 mx-1"></div>
                
                {/* Text Formatting */}
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => formatText('bold')}
                    className={`toolbar-btn ${formattingState.bold ? 'active' : ''}`}
                    title="Bold (Ctrl+B)"
                  >
                    <Bold className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => formatText('italic')}
                    className={`toolbar-btn ${formattingState.italic ? 'active' : ''}`}
                    title="Italic (Ctrl+I)"
                  >
                    <Italic className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => formatText('underline')}
                    className={`toolbar-btn ${formattingState.underline ? 'active' : ''}`}
                    title="Underline (Ctrl+U)"
                  >
                    <Underline className="w-4 h-4" />
                  </button>
                </div>
                
                <div className="w-px h-4 bg-gray-300 mx-1"></div>
                
                {/* Font Size */}
                <select
                  onChange={(e) => formatText('fontSize', e.target.value)}
                  className="text-xs border-0 bg-transparent focus:outline-none focus:ring-1 focus:ring-blue-500 rounded px-2 py-1"
                  title="Font Size"
                >
                  <option value="">Size</option>
                  <option value="1">Small</option>
                  <option value="3">Normal</option>
                  <option value="4">Medium</option>
                  <option value="5">Large</option>
                  <option value="6">X-Large</option>
                </select>
                
                {/* Text Color */}
                <input
                  type="color"
                  onChange={(e) => formatText('fontColor', e.target.value)}
                  className="w-8 h-8 border-0 rounded cursor-pointer"
                  title="Text Color"
                  defaultValue="#000000"
                />
                
                <div className="w-px h-4 bg-gray-300 mx-1"></div>
                
                {/* Alignment */}
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => formatText('alignLeft')}
                    className="toolbar-btn"
                    title="Align Left"
                  >
                    <AlignLeft className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => formatText('alignCenter')}
                    className="toolbar-btn"
                    title="Align Center"
                  >
                    <AlignCenter className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => formatText('alignRight')}
                    className="toolbar-btn"
                    title="Align Right"
                  >
                    <AlignRight className="w-4 h-4" />
                  </button>
                </div>
                
                <div className="w-px h-4 bg-gray-300 mx-1"></div>
                
                {/* Lists */}
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => formatText('unorderedList')}
                    className={`toolbar-btn ${formattingState.unorderedList ? 'active' : ''}`}
                    title="Bullet List"
                  >
                    <List className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => formatText('orderedList')}
                    className={`toolbar-btn ${formattingState.orderedList ? 'active' : ''}`}
                    title="Numbered List"
                  >
                    <ListOrdered className="w-4 h-4" />
                  </button>
                </div>
                
                <div className="w-px h-4 bg-gray-300 mx-1"></div>
                
                {/* Special Formatting */}
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => formatText('link')}
                    className="toolbar-btn"
                    title="Insert Link (Ctrl+K)"
                  >
                    <Link className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => formatText('blockquote')}
                    className={`toolbar-btn ${formattingState.blockquote ? 'active' : ''}`}
                    title="Quote"
                  >
                    <Quote className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => formatText('code')}
                    className={`toolbar-btn ${formattingState.code ? 'active' : ''}`}
                    title="Code"
                  >
                    <Code className="w-4 h-4" />
                  </button>
                </div>
                
                <div className="w-px h-4 bg-gray-300 mx-1"></div>
                
                {/* Remove Formatting */}
                <button
                  type="button"
                  onClick={() => formatText('removeFormat')}
                  className="toolbar-btn"
                  title="Remove Formatting"
                >
                  <Eraser className="w-4 h-4" />
                </button>
              </div>
              
              {/* Enhanced Gmail-Style Visual Editor */}
              <div className="relative">
                <div
                  ref={textareaRef}
                  contentEditable={true}
                  suppressContentEditableWarning={true}
                  onInput={(e) => {
                    updateContent();
                    // Update formatting state on content change
                    setTimeout(handleSelectionChange, 10);
                  }}
                  onPaste={handlePaste}
                  onKeyDown={(e) => {
                    // Handle keyboard shortcuts
                    if (e.ctrlKey || e.metaKey) {
                      switch (e.key) {
                        case 'b':
                          e.preventDefault();
                          formatText('bold');
                          break;
                        case 'i':
                          e.preventDefault();
                          formatText('italic');
                          break;
                        case 'u':
                          e.preventDefault();
                          formatText('underline');
                          break;
                        case 'k':
                          e.preventDefault();
                          formatText('link');
                          break;
                        case 'z':
                          e.preventDefault();
                          if (e.shiftKey) {
                            handleRedo(); // Ctrl+Shift+Z for redo
                          } else {
                            handleUndo(); // Ctrl+Z for undo
                          }
                          break;
                        case 'y':
                          e.preventDefault();
                          handleRedo(); // Ctrl+Y for redo (alternative)
                          break;
                        case 'Enter':
                          // Handle Enter key for better paragraph management
                          if (e.shiftKey) {
                            // Shift+Enter = line break
                            e.preventDefault();
                            document.execCommand('insertHTML', false, '<br>');
                          }
                          // Regular Enter = new paragraph (default behavior)
                          break;
                        default:
                          break;
                      }
                    }
                    
                    // Auto-detect URLs on space or enter
                    if (e.key === ' ' || e.key === 'Enter') {
                      setTimeout(() => {
                        const content = textareaRef.current.innerHTML;
                        const linkedContent = autoLinkUrls(content);
                        if (content !== linkedContent) {
                          textareaRef.current.innerHTML = linkedContent;
                          updateContent();
                        }
                      }, 10);
                    }
                    
                    // Update formatting state after key events
                    setTimeout(handleSelectionChange, 10);
                  }}
                  onClick={(e) => {
                    // Handle link clicks to open in new tab
                    if (e.target.tagName === 'A' && e.target.href) {
                      e.preventDefault();
                      setIsClickingLink(true);
                      
                      // Open the link
                      window.open(e.target.href, '_blank', 'noopener,noreferrer');
                      
                      // Reset the flag after a short delay
                      setTimeout(() => {
                        setIsClickingLink(false);
                      }, 100);
                    }
                  }}
                  onBlur={() => {
                    // Only auto-link URLs when editor loses focus if not clicking on a link
                    if (!linkDialog.isOpen && !isClickingLink) {
                      const content = textareaRef.current.innerHTML;
                      const linkedContent = autoLinkUrls(content);
                      if (content !== linkedContent) {
                        textareaRef.current.innerHTML = linkedContent;
                        updateContent();
                      }
                    }
                  }}
                  className="w-full px-4 py-3 overflow-y-auto focus:outline-none"
                  style={{ 
                    minHeight: '300px',
                    maxHeight: '400px',
                    fontSize: '14px',
                    lineHeight: '1.6',
                    color: '#202124',
                    fontFamily: 'system-ui, -apple-system, sans-serif'
                  }}
                  data-placeholder="Compose your email message here..."
                />
                
                {/* Gmail-Style Link Dialog */}
                {linkDialog.isOpen && (
                  <div className="absolute top-4 left-4 right-4 bg-white border border-gray-300 rounded-lg shadow-lg z-10 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-medium text-gray-900">
                        {linkDialog.selectedText 
                          ? `Add link to "${linkDialog.selectedText.substring(0, 30)}${linkDialog.selectedText.length > 30 ? '...' : ''}"` 
                          : 'Insert link'
                        }
                      </h3>
                      <button
                        onClick={handleLinkCancel}
                        className="text-gray-400 hover:text-gray-600 p-1"
                        title="Cancel"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    
                    <div className="mb-3">
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        URL
                      </label>
                      <input
                        type="text"
                        value={linkDialog.url}
                        onChange={(e) => setLinkDialog(prev => ({ ...prev, url: e.target.value }))}
                        onKeyDown={handleLinkKeyDown}
                        placeholder="Enter or paste a link"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        autoFocus
                      />
                    </div>
                    
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={handleLinkCancel}
                        className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleLinkSubmit}
                        disabled={!linkDialog.url.trim()}
                        className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                      >
                        Apply
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Attachments */}
            {attachments.length > 0 && (
              <div className="p-4 border-t border-gray-200">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Attachments</h4>
                <div className="flex flex-wrap gap-2">
                  {attachments.map(attachment => (
                    <div key={attachment.id} className="flex items-center gap-2 bg-gray-100 px-3 py-1 rounded-md">
                      <FileText className="w-4 h-4 text-gray-500" />
                      <span className="text-sm text-gray-700">{attachment.name}</span>
                      <button
                        onClick={() => removeAttachment(attachment.id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Gmail-Style Actions Footer */}
            <div className="compose-footer px-6 py-4">
              <div className="flex items-center justify-between">
                {/* Primary Send Button - Gmail Style */}
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleSend}
                    disabled={isLoading || !selectedEvent || recipientCount === 0}
                    className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2 text-sm font-medium shadow-sm"
                  >
                    <Send className="w-4 h-4" />
                    {isLoading ? 'Sending...' : 'Send'}
                  </button>
                  
                  <button
                    onClick={handleSaveDraft}
                    disabled={isLoading || !selectedEvent}
                    className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2 text-sm"
                  >
                    <Save className="w-4 h-4" />
                    Save Draft
                  </button>
                </div>

                {/* Secondary Actions */}
                <div className="flex items-center gap-2">
                  {/* Test Email */}
                  <div className="flex items-center gap-2">
                    <input
                      type="email"
                      value={testEmail}
                      onChange={(e) => setTestEmail(e.target.value)}
                      placeholder="test@example.com"
                      className="w-40 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <button
                      onClick={handleSendTest}
                      disabled={isLoading || !selectedEvent}
                      className="px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2 text-sm"
                    >
                      <TestTube className="w-4 h-4" />
                      Test
                    </button>
                  </div>
                  
                  {/* Attach Files */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    onChange={handleFileAttachment}
                    className="hidden"
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="p-2 text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                    title="Attach Files"
                  >
                    <Paperclip className="w-4 h-4" />
                  </button>
                  
                  {/* Preview */}
                  <button
                    onClick={handlePreview}
                    disabled={isLoading || !selectedEvent}
                    className="p-2 text-gray-600 hover:bg-gray-100 rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title="Preview"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar - Hidden on mobile, visible on large screens */}
          <div className="hidden lg:block lg:w-80 border-l border-gray-200 bg-gray-50 overflow-y-auto flex-shrink-0">
            <div className="p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Template Variables</h3>
              <div className="space-y-2">
                {Object.entries(templateVariables).map(([key, label]) => (
                  <button
                    key={key}
                    onClick={() => insertVariable(key)}
                    className="w-full text-left px-3 py-2 text-sm bg-white border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
                  >
                    <div className="font-medium text-gray-900">{label}</div>
                    <div className="text-xs text-gray-500">{`{{${key}}}`}</div>
                  </button>
                ))}
              </div>

              <div className="mt-6">
                <h3 className="text-sm font-medium text-gray-900 mb-3">Settings</h3>
                <div className="space-y-3">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={campaign.track_opens}
                      onChange={(e) => setCampaign(prev => ({ ...prev, track_opens: e.target.checked }))}
                      className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Track Opens</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={campaign.track_clicks}
                      onChange={(e) => setCampaign(prev => ({ ...prev, track_clicks: e.target.checked }))}
                      className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Track Clicks</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Email Preview</h3>
              <button
                onClick={() => setShowPreview(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 overflow-y-auto max-h-96">
              <div 
                className="prose max-w-none"
                dangerouslySetInnerHTML={{ __html: previewContent }}
              />
            </div>
          </div>
        </div>
      )}
      </div>
    </>
  );
};

export default EmailComposer;
